import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@heroui/link";
import { type ColumnDef } from "@tanstack/react-table";

import { ExternalLink } from "@/components/ExternalLink";
import { toWorkspacePath } from "@/features/Workspaces/workspaceRoutes";
import { formatNumber } from "@/features/Table/utils";
import { parseDate } from "@/lib/utils";

import {
  client,
  names,
  portfolio_table_name,
  static_data_table_name,
  table_name,
  type BankPositionRow,
} from "./config";

export function useBankPositionsColumns() {
  const { t } = useTranslation("tables");

  return useMemo<ColumnDef<BankPositionRow>[]>(
    () => [
      {
        accessorKey: "id",
        header: t(`${table_name}.columns.id`, { defaultValue: "ID" }),
      },
      {
        accessorKey: "date",
        header: t(`${table_name}.columns.date`, { defaultValue: "Date" }),
        cell: ({ row }) => (
          <div className="whitespace-nowrap">
            {parseDate(String(row.getValue("date")))}
          </div>
        ),
      },
      {
        accessorKey: names.portfolioClient,
        header: t(`${table_name}.columns.${names.portfolioClient}`, {
          defaultValue: "Client",
        }),
      },
      {
        accessorKey: names.portfolioName,
        header: t(`${table_name}.columns.${names.portfolioName}`, {
          defaultValue: "Account",
        }),
        cell: ({ row }) => (
          <ExternalLink
            href={toWorkspacePath(
              `/admin/${client}/${portfolio_table_name}/${row.original[names.portfolioId]}`,
            )}
            className="text-xs"
            underline="hover"
          >
            {String(row.getValue(names.portfolioName) ?? "")}
          </ExternalLink>
        ),
      },
      {
        accessorKey: names.staticDatumName,
        header: t(`${table_name}.columns.${names.staticDatumName}`, {
          defaultValue: "Asset",
        }),
        cell: ({ row }) => (
          <ExternalLink
            href={toWorkspacePath(
              `/admin/${client}/${static_data_table_name}/${row.original[names.staticDatumId]}`,
            )}
            className="text-xs"
            underline="hover"
          >
            {String(row.getValue(names.staticDatumName) ?? "")}
          </ExternalLink>
        ),
      },
      {
        accessorKey: "quantity",
        header: () => (
          <div className="text-right">
            {t(`${table_name}.columns.quantity`, { defaultValue: "Quantity" })}
          </div>
        ),
        cell: ({ row }) => (
          <div className="text-right">{formatNumber(row.getValue("quantity"))}</div>
        ),
      },
      {
        accessorKey: "market_value_local_ccy",
        header: () => (
          <div className="text-right">
            {t(`${table_name}.columns.market_value_local_ccy`, {
              defaultValue: "Valuation in local currency",
            })}
          </div>
        ),
        cell: ({ row }) => (
          <div className="text-right">
            {formatNumber(row.getValue("market_value_local_ccy"))}
          </div>
        ),
      },
      {
        accessorKey: "local_currency",
        header: t(`${table_name}.columns.local_currency`, {
          defaultValue: "Local currency",
        }),
      },
      {
        accessorKey: "market_value_ptf_ccy",
        header: () => (
          <div className="text-right">
            {t(`${table_name}.columns.market_value_ptf_ccy`, {
              defaultValue: "Valuation in reporting currency",
            })}
          </div>
        ),
        cell: ({ row }) => (
          <div className="text-right">
            {formatNumber(row.getValue("market_value_ptf_ccy"))}
          </div>
        ),
      },
      {
        accessorKey: names.portfolioReportingCurrency,
        header: t(`${table_name}.columns.${names.portfolioReportingCurrency}`, {
          defaultValue: "Portfolio reporting currency",
        }),
      },
      {
        accessorKey: "fixing_local_ccy",
        header: () => (
          <div className="text-right">
            {t(`${table_name}.columns.fixing_local_ccy`, {
              defaultValue: "Unit price in local currency",
            })}
          </div>
        ),
        cell: ({ row }) => (
          <div className="text-right">
            {formatNumber(row.getValue("fixing_local_ccy"))}
          </div>
        ),
      },
      {
        accessorKey: "fixing_ptf_ccy",
        header: () => (
          <div className="text-right">
            {t(`${table_name}.columns.fixing_ptf_ccy`, {
              defaultValue: "Unit price in reporting currency",
            })}
          </div>
        ),
        cell: ({ row }) => (
          <div className="text-right">
            {formatNumber(row.getValue("fixing_ptf_ccy"))}
          </div>
        ),
      },
      {
        id: "actions",
        cell: ({ row }) => (
          <div className="flex justify-end">
            <Link
              href={`${window.location.pathname}/${row.getValue("id")}`}
              className="text-xs"
              underline="hover"
            >
              {t("actions.edit", { ns: "translation", defaultValue: "Edit" })}
            </Link>
          </div>
        ),
      },
    ],
    [t],
  );
}
